import { DateRange } from "react-day-picker";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { months } from "./consts";
import { FilterDataSelected, FilterDataStringSelected, FilterRow } from "../components/Filters";

export const downloadFile = (url: string, fileName: string, openInNewTab: boolean = true) => {
  return new Promise((resolve, reject) => {
    if (url) {
      const link = document.createElement("a");
      link.href = url;

      if (openInNewTab) {
        link.target = "_blank";
      } else {
        link.setAttribute("download", fileName);
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Só revoga se for objeto blob (evita revogar links externos por engano)
      if (url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }

      setTimeout(resolve, 2000);
    } else {
      reject(new Error("Invalid URL"));
    }
  });
};

export const translateJurisdiction = (jurisdiction: string | undefined | null) => {
  switch (jurisdiction) {
    case "federal":
      return "Federal";
    case "state":
      return "Estadual";
    case "municipal":
      return "Municipal";
  }

  return "";
};

export const periodValid = (range: DateRange | undefined): boolean => {
  return !!range && !!range.from && !!range.to;
};

export class TimeoutError extends Error {
  constructor(message = "Tempo limite excedido") {
    super(message);
    this.name = "TimeoutError";
  }
}

/**
 * Garante que uma Promise resolva/rejeite em até `ms` milissegundos.
 * Se estourar o tempo, rejeita com `TimeoutError` — evitando que fluxos
 * de "pesquisar/atualizar em segundo plano" fiquem presos quando a
 * chamada trava ou não retorna. O timer é sempre limpo ao final.
 */
export const withTimeout = <T>(promise: Promise<T>, ms = 120000): Promise<T> => {
  let timer: ReturnType<typeof setTimeout>;

  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new TimeoutError()), ms);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
};

export const getTimeString = (seconds: number) => {
  const minutesResult = Math.floor(seconds / 60);
  const secondsResult = seconds % 60;

  return `${
    minutesResult > 0
      ? minutesResult + " minuto" + (minutesResult !== 1 ? "s" : "") + (secondsResult > 0 ? " e " : "")
      : ""
  }${secondsResult > 0 ? secondsResult + " segundo" + (secondsResult > 1 ? "s" : "") : ""}`;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatDateBR = (d?: Date) => (d ? d.toLocaleDateString("pt-BR").replace(/\//g, "-") : "");

export const formatTick = (value: any): string => {
  try {
    if (!value) {
      return "";
    }

    let [month, year] = value.split("/");

    month = months[Number(month) - 1];

    return `${month.substr(0, 3)}/${year}`;
  } catch (error) {
    console.log(error);
    return "";
  }
};

export const formatCurrency = (value: string | number) => {
  return Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value));
};

export const reduceString = (text: string | null, limit: number): string => {
  if (text === null) {
    return "";
  }

  if (text.length <= limit) {
    return text;
  } else {
    return text.substring(0, limit) + "...";
  }
};

export const hasAnyFilterApplied = (
  filterRows: FilterRow[],
  filterDataSelected: FilterDataSelected | null
): boolean => {
  if (!filterDataSelected) {
    return false;
  }

  if (filterRows.length === 0) {
    return false;
  }

  return filterRows.some((row) => {
    return row.rowFields.some((field) => {
      if (
        Array.isArray((filterDataSelected as FilterDataStringSelected)[field.name]) &&
        !(filterDataSelected as FilterDataStringSelected)[field.name][0]
      ) {
        return false;
      }

      return (
        filterDataSelected.hasOwnProperty(field.name) &&
        !!(filterDataSelected as FilterDataStringSelected)[field.name] &&
        ((filterDataSelected as FilterDataStringSelected)[field.name].length > 0 ||
          typeof (filterDataSelected as FilterDataStringSelected)[field.name] === "object")
      );
    });
  });
};
